import { apiError } from "../../../../../../utils/formErrors";
import React, { useState, useEffect, useMemo } from 'react';
import { Box, Stack, IconButton, Tooltip, Dialog } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import TransferAssetDialog from "../TransferAssetDialog";
import { useNavigate } from 'react-router';
import BtnPrimary from "../../../../../../component/PrimaryButton";
import BtnSecondary from "../../../../../../component/SecondaryButton";
import DataTable from "../../../../../../component/DataTable";
import { SearchInput, StatusChip } from "../../../../../../component/listing";
import { getSoftwareData, deleteAsset } from '../../../../../../services/asset';
import { useNotify } from '../../../../../../component/NotificationProvider';
import { tokens } from '../../../../../../theme';

const SoftwareListing = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [search, setSearch] = useState("");
  const [toDelete, setToDelete] = useState(null);
  // The asset whose entity is being changed. Separate from the delete
  // target: one is a correction, the other is a transfer between two
  // companies\' books.
  const [toTransfer, setToTransfer] = useState(null);

  const fetchData = async () => {
    setLoading(true);
    setError(false);
    try {
      const res = await getSoftwareData();
      if (res?.success === false) throw new Error(res?.message || "Failed to load software");
      setData(Array.isArray(res?.data) ? res.data : []);
    } catch (err) {
      setError(true);
      notify.error(err?.message || "Failed to load software");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, []);

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      const res = await deleteAsset(toDelete._id);
      if (res?.success === false) throw apiError(res, "Could not delete");
      notify.success("Software deleted");
      setData((prev) => prev.filter((s) => s._id !== toDelete._id));
    } catch (err) {
      notify.error(err?.message || "Could not delete software");
    } finally {
      setToDelete(null);
    }
  };

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return data;
    return data.filter((s) => [s.name, s.type, s.manufacturer, s.version].some((v) => (v || "").toLowerCase().includes(q)));
  }, [data, search]);

  return (
    <Box>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between" alignItems={{ md: "center" }} sx={{ mb: "1.25rem" }}>
        <SearchInput value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search software" minWidth="16rem" />
        <BtnPrimary to="form" sx={{ borderRadius: "8px", whiteSpace: "nowrap" }}>+ Add Software</BtnPrimary>
      </Stack>

      <DataTable
        rows={filtered}
        loading={loading}
        error={error}
        onRetry={fetchData}
        errorTitle="Couldn't load software"
        empty={{
          title: data.length === 0 ? "No software yet" : "No matches",
          description: data.length === 0 ? "Add your first software asset." : "Try a different search.",
        }}
        onRowClick={(row) => navigate("form", { state: { assetId: row._id, mode: "view" } })}
        tableSx={{ width: "100%", tableLayout: "fixed" }}
        columns={[
          { key: "name", label: "Name", width: "24%", cellSx: { fontWeight: 600 }, render: (row) => row.name || "—" },
          { key: "type", label: "Type", cellSx: { color: tokens.textMuted }, render: (row) => row.type || "—" },
          { key: "version", label: "Version", cellSx: { color: tokens.textMuted }, render: (row) => row.version || "—" },
          { key: "manufacturer", label: "Manufacturer", cellSx: { color: tokens.textMuted }, render: (row) => row.manufacturer || "—" },
          {
            key: "licensed", label: "License",
            render: (row) => <StatusChip label={row.licensed ? "Licensed" : "Free"} tone={row.licensed ? "info" : "muted"} />,
          },
          {
            key: "actions", label: "Actions", width: "5rem", align: "right",
            render: (row) => (
              <>
              <Tooltip title="Move to another legal entity">
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setToTransfer(row); }}
                  sx={{ color: tokens.textMuted, "&:hover": { color: tokens.accent } }}>
                  <SwapHorizIcon sx={{ fontSize: "1.1rem" }} />
                </IconButton>
              </Tooltip>
              <Tooltip title="Delete">
                {/* the row opens the software; the bin must not do both */}
                <IconButton size="small" onClick={(e) => { e.stopPropagation(); setToDelete(row); }}
                  sx={{ color: tokens.textMuted, "&:hover": { color: tokens.danger } }}>
                  <DeleteOutlineIcon sx={{ fontSize: "1.1rem" }} />
                </IconButton>
              </Tooltip>
              </>
            ),
          },
        ]}
      />


      <TransferAssetDialog
        asset={toTransfer}
        open={Boolean(toTransfer)}
        onClose={() => setToTransfer(null)}
        // The row's entity changed, so the list is stale — and the register may
        // be filtered to the entity it just left.
        onDone={fetchData}
      />

      <Dialog open={Boolean(toDelete)} onClose={() => setToDelete(null)} PaperProps={{ sx: { borderRadius: "12px", p: "1.5rem", maxWidth: "26rem" } }}>
        <Box sx={{ fontSize: "1rem", fontWeight: 600, color: tokens.text, mb: "0.5rem" }}>Delete software?</Box>
        <Box sx={{ fontSize: "0.875rem", color: tokens.textMuted, mb: "1.5rem" }}>
          {toDelete ? `"${toDelete.name}" will be permanently removed. This cannot be undone.` : ""}
        </Box>
        <Stack direction="row" justifyContent="flex-end" spacing={1}>
          <BtnSecondary onClick={() => setToDelete(null)} sx={{ borderRadius: "8px" }}>Cancel</BtnSecondary>
          <BtnSecondary onClick={confirmDelete} sx={{ borderRadius: "8px", color: tokens.danger, borderColor: tokens.dangerBorder, "&:hover": { borderColor: tokens.danger, background: tokens.dangerTint } }}>Delete</BtnSecondary>
        </Stack>
      </Dialog>
    </Box>
  );
};

export default SoftwareListing;
